<script setup lang="ts">
/**
 * Эпик-вью — макет 21:67: полоса прогресса по категориям статусов, дерево
 * историй и подзадач, справа состояние сервисов эпика.
 *
 * Эпик — та же задача, поэтому живёт по тому же адресу `/issue/DEV-1478`, но
 * показывает не карточку, а состояние: у эпика не бывает «своей» работы, у него
 * есть дети и то, что они уже выкатили.
 *
 * Ленты «События эпика» из макета здесь нет: общей ленты событий в трекере
 * нет — есть история одной задачи (docs/design-gaps.md, DEV-1500).
 */
import { AlertTriangle } from 'lucide-vue-next'
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import PipelineBadge from '@/features/issue/components/PipelineBadge.vue'
import IssueTypeIcon from '@/features/issues/components/IssueTypeIcon.vue'
import { useIssueStore } from '@/features/issue/store'

import {
  flatten,
  loadOps,
  loadPipelines,
  loadTree,
  toProgress,
  type EpicNode,
  type EpicOps,
} from '../api'

import type { SummaryRow } from '@/features/pipelines/api'

const emit = defineEmits<{ failed: [error: unknown] }>()

const { t } = useI18n()
const store = useIssueStore()

const nodes = ref<EpicNode[]>([])
const services = ref<SummaryRow[]>([])
const ops = ref<EpicOps>({ stands: [], releases: [] })
const isLoading = ref(false)

const issues = computed(() => flatten(nodes.value))
const progress = computed(() => toProgress(issues.value, store.columns))

/** Ширины сегментов полосы: считаем в процентах, чтобы полоса тянулась. */
const share = (count: number) =>
  progress.value.total ? `${(count / progress.value.total) * 100}%` : '0%'

const columnOf = (columnId: string) => store.columns.find((column) => column.id === columnId)

const load = async () => {
  const epic = store.issue

  if (!epic) return

  isLoading.value = true

  try {
    const tree = await loadTree(epic.projectId, epic.id)

    nodes.value = tree
    // Сводка и журнал не нужны, чтобы показать дерево: грузим их следом и не
    // задерживаем ими главное.
    const [pipelines, operations] = await Promise.all([
      loadPipelines(epic.projectId, epic.id),
      loadOps([epic.id, ...flatten(tree).map((issue) => issue.id)]),
    ])

    services.value = pipelines
    ops.value = operations
  } catch (failure) {
    emit('failed', failure)
  } finally {
    isLoading.value = false
  }
}

watch(() => store.issue?.id, load)

onMounted(load)
</script>

<template>
  <section class="epic">
    <div class="epic__progress">
      <p class="epic__summary">
        {{ t('epic.summary.total', progress.total) }} ·
        {{ t('epic.summary.done', { count: progress.done }) }} ·
        {{ t('epic.summary.inProgress', { count: progress.inProgress }) }} ·
        {{ t('epic.summary.open', { count: progress.open }) }}
      </p>

      <div class="epic__bar" role="img" :aria-label="t('epic.progressLabel')">
        <span class="epic__seg epic__seg--done" :style="{ width: share(progress.done) }" />
        <span
          class="epic__seg epic__seg--progress"
          :style="{ width: share(progress.inProgress) }"
        />
        <span class="epic__seg epic__seg--open" :style="{ width: share(progress.open) }" />
      </div>
    </div>

    <div class="epic__cols">
      <div class="epic__tree">
        <p v-if="!isLoading && !nodes.length" class="epic__empty">{{ t('epic.empty') }}</p>

        <template v-for="node in nodes" :key="node.issue.id">
          <RouterLink class="etree__row" :to="`/issue/${node.issue.key}`">
            <IssueTypeIcon :type="node.issue.type" />
            <span class="etree__key">{{ node.issue.key }}</span>
            <span class="etree__title">{{ node.issue.title }}</span>
            <span
              v-if="columnOf(node.issue.columnId)"
              class="etree__status"
              :style="{ color: columnOf(node.issue.columnId)?.color }"
            >
              {{ columnOf(node.issue.columnId)?.name }}
            </span>
          </RouterLink>

          <RouterLink
            v-for="child in node.children"
            :key="child.id"
            class="etree__row etree__row--child"
            :to="`/issue/${child.key}`"
          >
            <IssueTypeIcon :type="child.type" />
            <span class="etree__key">{{ child.key }}</span>
            <span class="etree__title">{{ child.title }}</span>
            <span
              v-if="columnOf(child.columnId)"
              class="etree__status"
              :style="{ color: columnOf(child.columnId)?.color }"
            >
              {{ columnOf(child.columnId)?.name }}
            </span>
          </RouterLink>
        </template>
      </div>

      <aside class="epic__side">
        <section class="epic__card">
          <h3 class="epic__card-title">{{ t('epic.pipelines') }}</h3>

          <p v-if="!services.length" class="epic__muted">{{ t('epic.noPipelines') }}</p>

          <div v-for="row in services" :key="row.run.id" class="esvc">
            <span class="esvc__name">{{ row.run.projectPath.split('/').pop() }}</span>
            <PipelineBadge
              :pipeline="{
                id: row.run.pipelineId,
                url: row.run.url,
                status: row.run.status,
                durationSeconds: row.run.durationSeconds,
                projectPath: row.run.projectPath,
                ref: row.run.ref,
              }"
            />
          </div>
        </section>

        <section v-if="ops.stands.length || ops.releases.length" class="epic__card">
          <h3 class="epic__card-title">{{ t('epic.ops') }}</h3>

          <div v-for="stand in ops.stands" :key="stand.id" class="eop">
            <span class="eop__name">{{ stand.branch }}</span>
            <span class="eop__badge eop__badge--live">{{ t('devops.envs.state.active') }}</span>
          </div>

          <div v-for="release in ops.releases" :key="release.id" class="eop">
            <span class="eop__name">
              {{ release.projectPath.split('/').pop() }} {{ release.tag }}
            </span>
            <span class="eop__badge">{{ t('devops.releases.tagged') }}</span>
          </div>
        </section>

        <p class="epic__note">
          <AlertTriangle :size="14" />
          <span>{{ t('epic.activityGap') }}</span>
        </p>
      </aside>
    </div>
  </section>
</template>

<style scoped>
.epic {
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-top: 20px;
}

.epic__summary {
  margin: 0 0 8px;
  color: var(--color-text-secondary);
  font-size: var(--font-size-body);
}

/* Полоса из трёх сегментов: готово, в работе, открыто — как в макете 21:126 */
.epic__bar {
  display: flex;
  gap: 2px;
  height: 8px;
  border-radius: var(--radius-full);
  overflow: hidden;
}

.epic__seg {
  height: 100%;
}

.epic__seg--done {
  background: var(--color-success);
}

.epic__seg--progress {
  background: var(--color-info);
}

.epic__seg--open {
  background: var(--color-border-strong, var(--color-border));
}

.epic__cols {
  display: flex;
  align-items: flex-start;
  gap: 24px;
}

.epic__tree {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-xl);
  background: var(--color-surface);
  overflow: hidden;
}

.etree__row {
  display: flex;
  align-items: center;
  gap: 10px;
  height: 40px;
  padding: 0 16px;
  border-bottom: 1px solid var(--color-border);
  color: inherit;
  text-decoration: none;
}

.etree__row:last-child {
  border-bottom: none;
}

.etree__row:hover {
  background: var(--color-bg-muted);
}

/* Подзадача отступает на 28 — как в макете 21:141 */
.etree__row--child {
  padding-left: 44px;
}

.etree__key {
  color: var(--color-text-primary);
  font-family: var(--font-family-mono);
  font-size: var(--font-size-body-sm);
}

.etree__title {
  flex: 1;
  overflow: hidden;
  color: var(--color-text-secondary);
  font-size: var(--font-size-body);
  white-space: nowrap;
  text-overflow: ellipsis;
}

.etree__status {
  font-size: var(--font-size-body-sm);
  white-space: nowrap;
}

.epic__side {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 360px;
  flex-shrink: 0;
}

.epic__card {
  display: flex;
  flex-direction: column;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-xl);
  background: var(--color-surface);
  overflow: hidden;
}

.epic__card-title {
  margin: 0;
  padding: 12px 16px;
  border-bottom: 1px solid var(--color-border);
  background: var(--color-bg-muted);
  color: var(--color-text-muted);
  font-family: var(--font-family-mono);
  font-size: var(--font-size-overline);
  font-weight: var(--font-weight-regular);
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.esvc,
.eop {
  display: flex;
  align-items: center;
  gap: 10px;
  height: 38px;
  padding: 0 16px;
  border-bottom: 1px solid var(--color-border);
}

.esvc:last-child,
.eop:last-child {
  border-bottom: none;
}

.esvc__name,
.eop__name {
  flex: 1;
  overflow: hidden;
  color: var(--color-text-primary);
  font-size: var(--font-size-body);
  white-space: nowrap;
  text-overflow: ellipsis;
}

.eop__badge {
  padding: 2px 8px;
  border-radius: var(--radius-sm);
  background: var(--color-bg-muted);
  color: var(--color-text-secondary);
  font-size: var(--font-size-overline);
  white-space: nowrap;
}

.eop__badge--live {
  background: var(--color-success-tint);
  color: var(--color-success-strong);
}

.epic__muted,
.epic__empty {
  margin: 0;
  padding: 12px 16px;
  color: var(--color-text-muted);
  font-size: var(--font-size-body-sm);
}

.epic__note {
  display: flex;
  gap: 8px;
  margin: 0;
  color: var(--color-text-muted);
  font-size: var(--font-size-body-sm);
}
</style>
